import * as React from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import { get, groupBy, isEmpty } from 'lodash';
import moment, { Moment } from 'moment';
import { useTranslation } from '@lib/useTypedTranslation';

import { IDeviceDischarge } from '../../services/core/devicePerformance';
import { DeviceEvent } from '../../services/core/eventsTypes';

import { StackedColumnsDateChart } from './stackedColumnsDateChart';
import { drawRelativeXAxisLabels } from './lib';
import { formatTooltipDay } from './tooltips';
import { IBatteryAverageDischargeData } from 'services/shared/battery/batteryCentricService';

interface IProps {
  data: IDeviceDischarge[] | IBatteryAverageDischargeData[],
  events?: DeviceEvent[],
  setDate?: (date: string | Moment) => void,
  className?: string,
  onMount?: (chart: am4charts.XYChart) => am4charts.XYChart,
  timePeriod?: number,
  title?: string,
  timeZoneOffset?: number,
  isLoading?: boolean,
  headerControls?: JSX.Element,
  disableLengthAdjustment?: boolean
}

export const handleBarClick = (event: any, setDate?: (date: string | Moment) => void) => {
  /* istanbul ignore next */
  if (setDate) {
    setDate(moment.utc(event.date));
  }
};

export function getAdjustChartDimensions(disableLengthAdjustment: boolean) {
  return { length: disableLengthAdjustment ? undefined : 97.5, height: 250 };

}

export function AverageDischargeChart(props: IProps) {
  const { t } = useTranslation('performance');
  const data = getData();
  const adjustChartDimensions = getAdjustChartDimensions(props.disableLengthAdjustment);

  function createGrid(value: number, yAxis: am4charts.ValueAxis): void {
    const range = yAxis.axisRanges.create();
    range.value = value;
    range.label = new am4charts.AxisLabel();
    range.label.dy = -18;
    range.label.text = "{value}";
  }

  /* istanbul ignore next */
  const onBarClick = (event: any) => handleBarClick(event, props?.setDate);

  return (
    <div className="average-discharge-chart-section">
      <StackedColumnsDateChart<any>
        headerControls={props.headerControls}
        bottomBorder={false}
        series={[{
          visible: true,
          dataKey: 'level',
          colour: '#1aa0d1',
          description: t('AVG_HOURLY_DISCHARGE')
        }]}
        onBarClick={onBarClick}
        className={props.className}
        cypressId="last30DaysAverageDischarge"
        data={data}
        days={30}
        title={props.title ?? ''}
        onMount={props.onMount}
        amchartsYAxisNumberFormat="#.00'%'"
        xAxisType='date'
        drawXAxis={(_, xAxis) => drawRelativeXAxisLabels(xAxis as am4charts.DateAxis, props.timePeriod ?? 30, props.timeZoneOffset ?? 0)}
        yAxisType={'value'}
        drawYAxis={(_, yAxis) => {
          yAxis.min = 0;
          yAxis.max = 100;
          yAxis.strictMinMax = true;
          yAxis.renderer.minGridDistance = 30;
          yAxis.numberFormatter.numberFormat = "#'%'";
          yAxis.cursorTooltipEnabled = false;
          yAxis.contentAlign = 'right';
          yAxis.renderer.grid.template.disabled = false;
          yAxis.renderer.labels.template.disabled = true;
          yAxis.axisRanges.clear();
          createGrid(0, yAxis);
          createGrid(25, yAxis);
          createGrid(50, yAxis);
          createGrid(75, yAxis);
          createGrid(100, yAxis);
        }}
        hasLegend={true}
        onlyShowCurrentSeriesInTooltip={true}
        useAxisTooltip
        eventOverlay={true}
        dateAxisEndDuration={{ amount: "1", unit: 'day' }}
        formatTooltipDate={formatTooltipDay}
        adjustChartDimensions={adjustChartDimensions}
        hasNoSelectionOverlay={!props?.isLoading}
      />
    </div>
  );

  function getData() {
    if (isEmpty(props.data) || props.data.length === 0) {
      return [];
    }

    const eventsByDay = groupBy(props.events, (event) => moment.utc(event.local).startOf('day').valueOf());
    return props.data.map(({ date, level }) => {
      return {
        date,
        level: level ?? 0,
        events: get(eventsByDay, date, [])
      };
    });
  }
}
